// components/Company/CompanyForm.jsx
import React, { useState } from 'react';
import { BuildingOffice2Icon, CheckCircleIcon, PhotoIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';

const timezones = [
  'UTC',
  'America/Adak', 'America/Anchorage', 'America/Chicago', 'America/Denver',
  'America/Detroit', 'America/Indiana/Indianapolis', 'America/Juneau',
  'America/Kentucky/Louisville', 'America/Los_Angeles', 'America/New_York',
  'America/Phoenix', 'America/Toronto', 'America/Vancouver',
  'Europe/Amsterdam', 'Europe/Athens', 'Europe/Berlin', 'Europe/Brussels',
  'Europe/Bucharest', 'Europe/Budapest', 'Europe/Copenhagen', 'Europe/Dublin',
  'Europe/Helsinki', 'Europe/Istanbul', 'Europe/Kiev', 'Europe/Lisbon',
  'Europe/London', 'Europe/Madrid', 'Europe/Moscow', 'Europe/Paris',
  'Europe/Prague', 'Europe/Rome', 'Europe/Stockholm', 'Europe/Vienna',
  'Europe/Warsaw', 'Europe/Zurich',
  'Asia/Bangkok', 'Asia/Beijing', 'Asia/Dubai', 'Asia/Hong_Kong',
  'Asia/Jakarta', 'Asia/Kolkata', 'Asia/Kuala_Lumpur', 'Asia/Macau',
  'Asia/Manila', 'Asia/Qatar', 'Asia/Riyadh', 'Asia/Seoul', 'Asia/Shanghai',
  'Asia/Singapore', 'Asia/Taipei', 'Asia/Tokyo',
  'Africa/Cairo', 'Africa/Casablanca', 'Africa/Johannesburg', 'Africa/Lagos',
  'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Melbourne',
  'Australia/Perth', 'Australia/Sydney',
  'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Guam', 'Pacific/Honolulu'
];

const languages = [
  { value: 'en', label: 'English' }, { value: 'es', label: 'Spanish' }, { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' }, { value: 'it', label: 'Italian' }, { value: 'pt', label: 'Portuguese' },
  { value: 'ru', label: 'Russian' }, { value: 'ja', label: 'Japanese' }, { value: 'ko', label: 'Korean' },
  { value: 'zh', label: 'Chinese (Simplified)' }, { value: 'zh-TW', label: 'Chinese (Traditional)' }, { value: 'ar', label: 'Arabic' },
  { value: 'hi', label: 'Hindi' }, { value: 'bn', label: 'Bengali' }, { value: 'pa', label: 'Punjabi' },
  { value: 'te', label: 'Telugu' }, { value: 'mr', label: 'Marathi' }, { value: 'ta', label: 'Tamil' },
  { value: 'ur', label: 'Urdu' }, { value: 'gu', label: 'Gujarati' }, { value: 'kn', label: 'Kannada' },
  { value: 'or', label: 'Odia' }, { value: 'ml', label: 'Malayalam' }, { value: 'tr', label: 'Turkish' },
  { value: 'vi', label: 'Vietnamese' }, { value: 'th', label: 'Thai' }, { value: 'fa', label: 'Persian' },
  { value: 'pl', label: 'Polish' }, { value: 'nl', label: 'Dutch' }, { value: 'sv', label: 'Swedish' },
  { value: 'da', label: 'Danish' }, { value: 'no', label: 'Norwegian' }, { value: 'fi', label: 'Finnish' },
  { value: 'cs', label: 'Czech' }, { value: 'hu', label: 'Hungarian' }, { value: 'el', label: 'Greek' },
  { value: 'he', label: 'Hebrew' }, { value: 'id', label: 'Indonesian' }, { value: 'ms', label: 'Malay' },
  { value: 'fil', label: 'Filipino' }
];

const industries = [
  'Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing',
  'Retail', 'Real Estate', 'Transportation', 'Energy', 'Hospitality',
  'Construction', 'Agriculture', 'Entertainment', 'Media', 'Telecommunications',
  'Pharmaceuticals', 'Automotive', 'Aerospace', 'Defense', 'Biotechnology',
  'Insurance', 'Legal', 'Consulting', 'Accounting', 'Marketing',
  'Advertising', 'Non-Profit', 'Government', 'Utilities', 'Mining',
  'Logistics', 'Tourism', 'Food & Beverage', 'Fashion', 'Sports',
  'Gaming', 'E-commerce', 'Software', 'Hardware', 'Cloud Services',
  'Cybersecurity', 'Artificial Intelligence', 'Blockchain', 'Renewable Energy',
  'Environmental Services', 'Healthcare Services', 'Financial Services',
  'Educational Services', 'Professional Services', 'Other'
];

const CompanyForm = ({ onSubmit, onCancel, initialData, isEditing = false }) => {
  const [formData, setFormData] = useState(initialData || {
    name: '', email: '', phone: '', address: '', website: '',
    industry: '', foundedYear: '', companySize: '', description: '',
    missionStatement: '', visionStatement: '', values: '', socialMediaLinks: '',
    contactPersonName: '', contactPersonEmail: '', contactPersonPhone: '',
    timezone: 'UTC', language: 'en', logoUrl: '', isActive: true
  });

  const [logoPreview, setLogoPreview] = useState(initialData?.logoUrl || '');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, logoUrl: file }));
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleTextAreaChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Company name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (formData.website && !/^https?:\/\/.+\..+/.test(formData.website)) newErrors.website = 'Website URL is invalid';
    if (formData.contactPersonEmail && !/\S+@\S+\.\S+/.test(formData.contactPersonEmail))
      newErrors.contactPersonEmail = 'Contact person email is invalid';
    if (formData.foundedYear && (formData.foundedYear < 1800 || formData.foundedYear > new Date().getFullYear()))
      newErrors.foundedYear = 'Invalid founded year';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const cleanedData = {
        ...formData,
        foundedYear: formData.foundedYear ? parseInt(formData.foundedYear) : null,
        companySize: formData.companySize ? parseInt(formData.companySize) : null
      };
      await onSubmit(cleanedData);
    } catch (error) {
      setErrors({ general: error.response?.data?.message || 'Error saving company' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="flex flex-col lg:flex-row gap-6 bg-gradient-to-br from-[var(--tint)] via-white to-[var(--tint)] p-6 rounded-2xl shadow-md">
      {/* Left Panel */}
      <div className="w-full lg:w-1/4 bg-gradient-to-br from-[var(--tint)] via-white to-[var(--tint)] rounded-2xl shadow-md p-6 border border-[var(--tint-line)] h-fit">
        <img
          src="/src/assets/Company/companyleft.png"
          alt="Company Settings"
          className="w48 h-48 object-contain mb-3"
        />
        <h2 className="text-lg font-semibold text-gray-800">Company Settings</h2>
        <p className="text-gray-500 text-sm mt-1 leading-snug">
          Configure company details, upload logo, and manage preferences.
        </p>
      </div>

      {/* Right Form Panel */}
      <motion.form
        onSubmit={handleSubmit}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex-1 bg-gradient-to-br from-[var(--tint)] via-white to-[var(--tint)] rounded-xl shadow-sm p-6 border border-[var(--tint-line)]"
      >
        {errors.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
            <p className="text-red-700 text-sm">{errors.general}</p>
          </div>
        )}

        {/* Logo Upload */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Company Logo"
                className="w-32 h-32 object-cover rounded-2xl shadow-lg border-2 border-gray-200"
              />
            ) : (
              <div className="w-32 h-32 flex flex-col items-center justify-center rounded-2xl bg-gray-100 border-2 border-dashed border-gray-300 text-gray-400">
                <PhotoIcon className="w-8 h-8 mb-2" />
                <span className="text-xs text-center">No Logo</span>
              </div>
            )}
          </div>
          <label className="mt-4 cursor-pointer bg-[var(--accent)] text-white px-4 py-2 rounded-lg hover:bg-[var(--accent-strong)] transition-colors text-sm font-medium">
            Upload Logo
            <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
          </label>
        </div>

        {/* Basic Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {[
            { label: 'Company Name *', name: 'name', type: 'text', required: true, placeholder: 'Enter company name' },
            { label: 'Email *', name: 'email', type: 'email', required: true, placeholder: 'company@example.com' },
            { label: 'Phone', name: 'phone', type: 'tel', placeholder: '+1 (555) 000-0000' },
            { label: 'Website', name: 'website', type: 'url', placeholder: 'https://example.com' },
          ].map((field) => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {field.label}
              </label>
              <input
                type={field.type}
                name={field.name}
                value={formData[field.name] || ''}
                onChange={handleChange}
                required={field.required}
                placeholder={field.placeholder}
                className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 transition-colors ${
                  errors[field.name] ? 'border-red-500' : ''
                }`}
              />
              {errors[field.name] && (
                <p className="text-red-500 text-sm mt-1">{errors[field.name]}</p>
              )}
            </div>
          ))}

          {/* Industry and Company Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Industry</label>
            <select
              name="industry"
              value={formData.industry || ''}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm bg-white"
            >
              <option value="">Select Industry</option>
              {industries.map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Founded Year</label>
            <input
              type="number"
              name="foundedYear"
              value={formData.foundedYear || ''}
              onChange={handleChange}
              min="1800"
              max={currentYear}
              placeholder="2020"
              className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 ${
                errors.foundedYear ? 'border-red-500' : ''
              }`}
            />
            {errors.foundedYear && (
              <p className="text-red-500 text-sm mt-1">{errors.foundedYear}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Size</label>
            <input
              type="number"
              name="companySize"
              value={formData.companySize || ''}
              onChange={handleChange}
              min="1"
              placeholder="50"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
            <select
              name="timezone"
              value={formData.timezone}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm bg-white"
            >
              {timezones.map(tz => (
                <option key={tz} value={tz}>
                  {tz.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
            <select
              name="language"
              value={formData.language}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm bg-white"
            >
              {languages.map(l => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Contact Person Information */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Contact Person</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name</label>
              <input
                type="text"
                name="contactPersonName"
                value={formData.contactPersonName || ''}
                onChange={handleChange}
                placeholder="John Doe"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
              <input
                type="email"
                name="contactPersonEmail"
                value={formData.contactPersonEmail || ''}
                onChange={handleChange}
                placeholder="contact@example.com"
                className={`w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 ${
                  errors.contactPersonEmail ? 'border-red-500' : ''
                }`}
              />
              {errors.contactPersonEmail && (
                <p className="text-red-500 text-sm mt-1">{errors.contactPersonEmail}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
              <input
                type="tel"
                name="contactPersonPhone"
                value={formData.contactPersonPhone || ''}
                onChange={handleChange}
                placeholder="+1 (555) 000-0000"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800"
              />
            </div>
          </div>
        </div>

        {/* Text Areas for Descriptions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              name="description"
              value={formData.description || ''}
              onChange={(e) => handleTextAreaChange('description', e.target.value)}
              rows={4}
              placeholder="Brief description of the company..."
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
            <textarea
              name="address"
              value={formData.address || ''}
              onChange={(e) => handleTextAreaChange('address', e.target.value)}
              rows={4}
              placeholder="Company physical address..."
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 resize-none"
            />
          </div>
        </div>

        {/* Mission, Vision, Values */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Mission Statement</label>
            <textarea
              name="missionStatement"
              value={formData.missionStatement || ''}
              onChange={(e) => handleTextAreaChange('missionStatement', e.target.value)}
              rows={3}
              placeholder="Our company's mission..."
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Vision Statement</label>
            <textarea
              name="visionStatement"
              value={formData.visionStatement || ''}
              onChange={(e) => handleTextAreaChange('visionStatement', e.target.value)}
              rows={3}
              placeholder="Our company's vision..."
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Core Values</label>
            <textarea
              name="values"
              value={formData.values || ''}
              onChange={(e) => handleTextAreaChange('values', e.target.value)}
              rows={3}
              placeholder="Our company's core values..."
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 resize-none"
            />
          </div>
        </div>

        {/* Social Media Links */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Social Media Links</label>
          <textarea
            name="socialMediaLinks"
            value={formData.socialMediaLinks || ''}
            onChange={(e) => handleTextAreaChange('socialMediaLinks', e.target.value)}
            rows={2}
            placeholder="Facebook: https://facebook.com/company&#10;LinkedIn: https://linkedin.com/company&#10;Twitter: https://twitter.com/company"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)] outline-none shadow-sm text-gray-800 resize-none"
          />
          <p className="text-xs text-gray-500 mt-1">Enter each social media link on a new line</p>
        </div>

        {/* Active Status */}
        {isEditing && (
          <div className="flex items-center space-x-3 mb-6 p-4 bg-gray-50 rounded-lg">
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="w-5 h-5 text-[var(--accent)] rounded border-gray-300 focus:ring-[var(--accent)]"
            />
            <label className="text-sm font-medium text-gray-700">Active Company</label>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-3 bg-[var(--accent)] text-white rounded-lg hover:bg-[var(--accent-strong)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"></span>
                Saving...
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5" />
                {isEditing ? 'Update Company' : 'Save Company'}
              </>
            )}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

export default CompanyForm;